import { FlatObject } from './helpers';

// https://stackoverflow.com/questions/901115/how-can-i-get-query-string-values-in-javascript
export const getQueryStringParams = (qs?: string): Record<string, string> => {
  if (!qs) {
    qs = window.location.search.substring(1);
  }

  let match;
  const pl = /\+/g, // Regex for replacing addition symbol with a space
    search = /([^&=]+)=?([^&]*)/g,
    decode = (s: string) => decodeURIComponent(s.replace(pl, ' '));
  const urlParams: FlatObject = {};
  while ((match = search.exec(qs))) {
    urlParams[decode(String(match[1]))] = decode(String(match[2]));
  }

  return urlParams;
};

export const makeQueryString = (params: FlatObject): string => {
  return Object.keys(params)
    .map((key) => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');
};

export const addUrlQueryParam = (key: string, value: string, replace = false): void => {
  const qs = getQueryStringParams();
  qs[key] = value;

  const path = window.location.href.split('?').shift();
  const url = `${path}?${makeQueryString(qs)}`;

  if (replace) {
    window.history.replaceState(null, '', url);
  } else {
    window.history.pushState(null, '', url);
  }
};

export const patchQueryParams = (updates: Record<string, string | null>, replace = false): void => {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  Object.entries(updates).forEach(([key, value]) => {
    if (value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });

  url.search = params.toString();

  if (replace) {
    window.history.replaceState(null, '', url.toString());
  } else {
    window.history.pushState(null, '', url.toString());
  }
};

export const removeUrlQueryParam = (key: string) => {
  const url = new URL(window.location.href);
  const { searchParams } = url;
  url.searchParams.delete(key);
  url.search = searchParams.toString();
  window.history.replaceState({}, document.title, url.toString());
};

export const getUrlQueryParam = (key: string): string | null => {
  const url = new URL(window.location.href);
  const val = url.searchParams.get(key);
  return val ? decodeURIComponent(val) : null;
};

// https://stackoverflow.com/questions/4907843/open-a-url-in-a-new-tab-and-not-a-new-window
export const openNewTab = (url: string, target = '_blank'): void => {
  window.open(url, target)?.focus();
};

export const getHost = (): string => String(window.location.origin).replace(/^http(s?):\/\//, '');

export const getPathSegments = (): string[] =>
  window.location.pathname.split('/').filter(Boolean);

export const pathIsActive = (path: string, matchPrefix?: boolean): boolean => {
  const currentPath = String(window.location.pathname);
  return currentPath === path || (!!matchPrefix && currentPath.startsWith(path));
};

export const pathIsExternal = (path: string): boolean =>
  Boolean(String(path).match(/^http(s?):\/\//));

export const withoutProto = (s: string): string => s.replace(/^http(s?):\/\//, '');

// Common multi-part TLDs (ccSLDs) for registrable domain extraction. avoids dependencies for very good coverage.
const MULTI_PART_TLDS = new Set([
  'co.uk', 'ac.uk', 'gov.uk', 'org.uk', 'me.uk', 'net.uk',
  'com.au', 'net.au', 'org.au', 'gov.au', 'edu.au',
  'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br',
  'co.jp', 'com.jp', 'org.jp', 'go.jp', 'ac.jp', 'ne.jp',
  'co.nz', 'org.nz', 'gov.nz', 'ac.nz', 'net.nz',
  'co.in', 'com.in', 'org.in', 'gov.in', 'net.in',
  'com.mx', 'org.mx', 'gov.mx', 'net.mx',
  'co.kr', 'or.kr', 'go.kr', 'ac.kr',
  'com.cn', 'org.cn', 'gov.cn', 'net.cn',
  'co.za', 'org.za', 'gov.za', 'net.za',
  'com.sg', 'org.sg', 'gov.sg', 'edu.sg',
  'com.hk', 'org.hk', 'gov.hk', 'edu.hk',
  'com.tw', 'org.tw', 'gov.tw', 'edu.tw',
  'co.il', 'org.il', 'gov.il', 'ac.il',
  'co.th', 'or.th', 'go.th', 'ac.th',
  'com.ar', 'org.ar', 'gov.ar', 'edu.ar',
  'com.my', 'org.my', 'gov.my', 'edu.my',
  'co.id', 'or.id', 'go.id', 'ac.id',
  'com.ph', 'org.ph', 'gov.ph', 'edu.ph',
  'com.vn', 'org.vn', 'gov.vn', 'edu.vn',
  'co.ke', 'or.ke', 'go.ke', 'ac.ke',
  'com.ng', 'org.ng', 'gov.ng', 'edu.ng',
  'com.eg', 'org.eg', 'gov.eg', 'edu.eg',
  'com.pk', 'org.pk', 'gov.pk', 'edu.pk',
  'com.ua', 'org.ua', 'gov.ua', 'edu.ua',
  'com.tr', 'org.tr', 'gov.tr', 'edu.tr',
]);

export const getDomain = (source: string): string => {
  const hostname = getHostname(source);
  if (!hostname) {
    return '';
  }

  const parts = hostname.split('.');
  if (parts.length <= 2) {
    return hostname;
  }

  const lastTwo = parts.slice(-2).join('.');
  if (MULTI_PART_TLDS.has(lastTwo)) {
    return parts.slice(-3).join('.');
  }

  return parts.slice(-2).join('.');
};

export const getHostname = (source: string): string => {
  try {
    const sourceUrl = new URL(source);
    return sourceUrl?.hostname || '';
  } catch {
    return '';
  }
};

export const getArrayParam = (key: string, vals: (string | number)[]): URLSearchParams => {
  const params = new URLSearchParams();

  vals.forEach((val) => {
    params.append(key, String(val));
  });

  return params;
};

export const addProtocol = (url: string): string => {
  if (!url || url.match(/^http(s?):\/\//)) {
    return url;
  }

  return `https://${url}`;
};

export const getHash = (): string => String(window.location.hash).replace(/^#/, '');

export const getHashParams = (): Record<string, string> => {
  const hashVal = getHash();

  if (!hashVal) {
    return {};
  }

  return getQueryStringParams(hashVal);
};

export const updateHashParams = (
  hashParams: Record<string, string>,
  addHistory?: boolean
): void => {
  const hashVal = Object.keys(hashParams).length ? `#${makeQueryString(hashParams)}` : '';

  if (addHistory) {
    window.location.hash = hashVal;
    return;
  }

  const url = new URL(window.location.href);
  url.hash = hashVal;
  window.history.replaceState(null, '', url.toString());
};
